import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const formatDateTimeToUserLocale = (date: string, customFormat?: string | null) => {
  return dayjs
    .utc(date)
    .local()
    .format(customFormat || 'YYYY-MM-DD HH:mm:ss');
};

const OFFSET_PATTERN = /([+-])(\d{2}):?(\d{2})$/;

/**
 * Format an ISO timestamp using the timezone offset embedded in the string (e.g. the deployment
 * region the backend stamped on trust-signal evidence), NOT the viewer's browser timezone — so a
 * reviewer always reads the student's local calendar regardless of where they sit. Falls back to
 * UTC when the string carries no offset (or ends in `Z`).
 */
export const formatDateTimePreserveZone = (date: string, customFormat?: string | null) => {
  const match = OFFSET_PATTERN.exec(date);
  const offsetMinutes = match
    ? (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3]))
    : 0;

  return dayjs
    .utc(date)
    .utcOffset(offsetMinutes)
    .format(customFormat || 'YYYY-MM-DD HH:mm:ss');
};

// Coarse review-table locality: "12m ago", "5h ago", "3d ago", "4mo ago", "2y ago".
export const formatRelativeAge = (iso: string): string => {
  const created = dayjs(iso);
  const minutes = Math.max(0, dayjs().diff(created, 'minute'));
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = dayjs().diff(created, 'hour');
  if (hours < 48) {
    return `${hours}h ago`;
  }
  const days = dayjs().diff(created, 'day');
  if (days < 60) {
    return `${days}d ago`;
  }
  const months = dayjs().diff(created, 'month');
  if (months < 12) {
    return `${months}mo ago`;
  }

  return `${dayjs().diff(created, 'year')}y ago`;
};
